import { ERROR_CODE } from '@common/constants';
import { ErrorOption, ToastMessage, toastMessage } from '@gcl/react-core';
import { useMySignatureModal } from '@userProfile/MySignatureModal.hooks';
import * as React from 'react';
import { AxiosErrorResponse, IErrorResponsePair } from './error.models';
import { apiErrorHandle } from './error.services';
import './ApiErrorDisplay.scss';
import { useAppConfigs } from '@config/config.hooks';
import { ReportErrorButton } from '@common/error/ReportErrorButton';

export interface IApiErrorDisplayProps {
  errors: IErrorResponsePair[];
  setFormError?: (
    name: string,
    error: ErrorOption,
    options?:
      | {
          shouldFocus: boolean;
        }
      | undefined,
  ) => void;
  callbackAfterToast?: () => void;
}

export const ApiErrorDisplay: React.FC<IApiErrorDisplayProps> = ({ errors = [], setFormError, callbackAfterToast }) => {
  const appConfigs = useAppConfigs();
  const { openPortal: openMySignatureModal, renderModal: renderMySignatureModal } = useMySignatureModal({});

  // If there are multiple response errors, only display the first one
  const error = errors.filter((e) => {
    return !!e?.error;
  })?.[0];

  React.useEffect(() => {
    handleError(error, appConfigs.configs.traceId, openMySignatureModal, setFormError, callbackAfterToast);
  }, [JSON.stringify(error)]);

  return <>{renderMySignatureModal()}</>;
};

const handleError = (
  error: IErrorResponsePair,
  traceId: string,
  openMySignatureModal?: () => void,
  setFormError?: (
    name: string,
    error: ErrorOption,
    options?:
      | {
          shouldFocus: boolean;
        }
      | undefined,
  ) => void,
  callbackAfterToast?: () => void,
) => {
  if (typeof error === 'string') {
    toastMessage(
      <ToastMessage message={error} toastOptions={{ type: 'error', closeOnClick: true, autoClose: false }} />,
    );
    return;
  }
  if (!error?.error) return;
  const { errorType, errorMessage, formErrors, errorCode } = apiErrorHandle(
    error?.error as AxiosErrorResponse,
    error?.genericError,
  );
  if (errorType === 'DirectDisplayError') {
    if (errorCode === ERROR_CODE.ERR_SIGN_NOT_FOUND && openMySignatureModal) {
      openMySignatureModal();
    } else if (errorMessage) {
      toastMessage(
        <ToastMessage message={errorMessage} toastOptions={{ type: 'error', closeOnClick: true, autoClose: false }} />,
      );
    } else {
      showToastWithReport(error, traceId);
    }
  }
  if (errorType === 'FormError') {
    if (formErrors && setFormError) {
      Object.keys(formErrors).forEach((key) => {
        const keyLocal = error.keyExtractor ? error.keyExtractor(key) : key;
        setFormError(keyLocal, {
          message: formErrors[key],
        });
      });
    } else if (!setFormError) {
      showToastWithReport(error, traceId);
    }
  }

  if (error.reset) error.reset();

  if (callbackAfterToast && typeof callbackAfterToast === 'function') callbackAfterToast();
};

export const showToastWithReport = (error: IErrorResponsePair, traceId: string) => {
  toastMessage(
    <ToastMessage
      message={
        <div className="gcl-gems-api-error-display-container">
          <div>{error.genericError}</div>
          <ReportErrorButton errorMessage={error.genericError} traceId={traceId} />
        </div>
      }
      toastOptions={{ type: 'error', closeOnClick: true, autoClose: false }}
    />,
  );
};

export const showToast = (
  error: IErrorResponsePair,
  traceId: string,
  setFormError?: (
    name: string,
    error: ErrorOption,
    options?:
      | {
          shouldFocus: boolean;
        }
      | undefined,
  ) => void,
  callbackAfterToast?: () => void,
) => {
  handleError(error, traceId, undefined, setFormError, callbackAfterToast);
};
